import { MAX_ZONES_PER_CONDENSER } from 'data/constants';
import type {
  DeadEndCopyType,
  RecoveryFilterControlType,
  ValidSystemsDeadEndReasonType,
  ValidSystemsRecoveryFilterType
} from 'types/dead-end';

// The reason codes and recovery filters at runtime, for the reason guard
// (utils/dead-end-reason.ts) and the tests. They cannot be read off
// @alpinehomeair/common's zod enums: the package's browser entry
// (build/browser.js, what webpack bundles) exports types and helpers only,
// no schemas — tsc and Jest resolve build/index.js and would not notice.
// So each list is the key set of a Record over the published union, which
// compile-links it both ways: a value common adds is a missing property
// here, a value common does not know is an excess one.
const DEAD_END_REASONS_LISTED: Record<ValidSystemsDeadEndReasonType, true> = {
  no_condensers: true,
  missing_system_input: true,
  unsupported_zone_count: true,
  no_catalog_match: true,
  filters_excluded_all: true,
  scoring_unavailable: true,
  no_cross_condenser_combination: true,
  unknown: true
};

export const VALID_SYSTEMS_DEAD_END_REASONS = Object.keys(
  DEAD_END_REASONS_LISTED
) as ReadonlyArray<ValidSystemsDeadEndReasonType>;

const RECOVERY_FILTERS_LISTED: Record<ValidSystemsRecoveryFilterType, true> = {
  inStock: true,
  onSale: true,
  price: true,
  condenserSeries: true,
  condenserBtus: true,
  hyperHeat: true,
  systemTypes: true,
  refrigerants: true,
  airHandlerTypes: true
};

export const VALID_SYSTEMS_RECOVERY_FILTERS = Object.keys(
  RECOVERY_FILTERS_LISTED
) as ReadonlyArray<ValidSystemsRecoveryFilterType>;

// How the filter panel labels each recoverable filter, so a dead-end hint
// that names one reads the way the customer sees it (system-filters-panel,
// equipment-filters-menu). Exhaustive over the recovery-filter union.
export const recoveryFilterLabels: Record<
  ValidSystemsRecoveryFilterType,
  string
> = {
  inStock: 'In stock only',
  onSale: 'On sale only',
  price: 'Price Range',
  condenserSeries: 'Condenser Series',
  condenserBtus: 'Condenser Size',
  hyperHeat: 'Capability',
  systemTypes: 'System Type',
  refrigerants: 'Refrigerant',
  airHandlerTypes: 'Air Handler Type'
};

// How the panel lets the customer change each recoverable filter: a switch,
// the two price inputs, or a checkbox section where every option starts
// checked and unchecking narrows (so "clearing" the filter means checking
// them all again, not unchecking them). Exhaustive over the union.
export const recoveryFilterControls: Record<
  ValidSystemsRecoveryFilterType,
  RecoveryFilterControlType
> = {
  inStock: 'toggle',
  onSale: 'toggle',
  price: 'range',
  condenserSeries: 'checkboxes',
  condenserBtus: 'checkboxes',
  hyperHeat: 'checkboxes',
  systemTypes: 'checkboxes',
  refrigerants: 'checkboxes',
  airHandlerTypes: 'checkboxes'
};

// The first sentence of every filters_excluded_all hint; deadEndCopyFor
// (utils/dead-end-copy.ts) follows it with the customer's action on the
// named filter when the diagnosis carries one, and deadEndCopy below with
// the generic ask when it does not (no single filter brings systems back).
export const FILTERS_HIDE_SYSTEMS_LEAD =
  'Systems exist for this design, but the selected filters hide all of them.';

// Every word the customer reads for the selector's dead-end diagnosis (spec
// 001 T003): a plain-English title and hint per reason code. The API sends
// codes and facts only (response.deadEnd), so the copy is owned here. The
// Record is exhaustive over the reason union, so a new code in the
// published enum fails to compile here until it has copy. T007+ attach the
// matching recover- or handoff action under the same reason.
export const deadEndCopy: Record<
  ValidSystemsDeadEndReasonType,
  DeadEndCopyType
> = {
  no_condensers: {
    title: 'We cannot match systems without an outdoor unit',
    hint: 'No outdoor unit is placed in this design yet.'
  },
  missing_system_input: {
    title: 'An outdoor unit is missing its rooms',
    hint: 'An outdoor unit has no rooms assigned to it yet.'
  },
  unsupported_zone_count: {
    title: 'This design has a zone count one outdoor unit cannot serve',
    hint: `An outdoor unit can serve between 1 and ${MAX_ZONES_PER_CONDENSER} zones. Split the zones across another outdoor unit.`
  },
  no_catalog_match: {
    title: 'A zone may need a size or type we do not carry',
    hint: 'No equipment fits every zone on this outdoor unit. A zone load may be larger or smaller than any indoor unit of the allowed types; allow more indoor unit types, split the room, or ask a design consultant.'
  },
  filters_excluded_all: {
    title: 'Your filters are hiding the systems',
    hint: `${FILTERS_HIDE_SYSTEMS_LEAD} Clear one or more filters.`
  },
  scoring_unavailable: {
    title: 'We cannot rank the systems right now',
    hint: 'Systems were found but could not be ranked just now. Try again in a moment.'
  },
  no_cross_condenser_combination: {
    title: 'No combination covers every outdoor unit',
    hint: 'Each outdoor unit has options, but no combination satisfies every outdoor unit together.'
  },
  unknown: {
    title: 'We could not find a system for this design',
    hint: 'No system matched this design.'
  }
};
